import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { useTenant } from './hooks/useTenant';
import { useIdleTimeout } from './hooks/useIdleTimeout';
import { LoadingScreen } from './components/common/LoadingSpinner';
import { isNativeApp, nativeAppMode } from '@pp/shared/platform/platform';
import { isBiometricEnabled } from '@pp/shared/auth/biometricAuth';
import { useAppStore } from './contexts/store';

// Eager layout shells + routing primitives. Kept eager because they render
// on every protected navigation — there's no benefit to splitting them off,
// and putting them in their own chunks would just add cascading import
// rountrips on every section change.
import { PublicLayout } from './components/layout/PublicLayout';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { FieldLayout } from './components/layout/FieldLayout';
import { PortalLayout } from './components/layout/PortalLayout';
import { ContractorLayout } from './components/layout/ContractorLayout';
import { PrivateRoute } from './components/routing/PrivateRoute';
import { PortalPrivateRoute } from './components/portal/PortalPrivateRoute';
import { ContractorPrivateRoute } from './components/contractor/ContractorPrivateRoute';
import { RequirePermission } from './components/RequirePermission';
import { AppProviders } from './components/providers/AppProviders';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { ScrollToTop } from './components/common/ScrollToTop';
import { AnalyticsTracker } from './components/common/AnalyticsTracker';
import { LimitExceededModal } from './components/common/LimitExceededModal';
import { PushTapHandler } from './components/PushTapHandler';

// Eager critical-path auth screens. BiometricUnlock is the native cold-start
// landing; LoginPage is the password fallback when Face ID fails. Both are
// small and need to paint without an extra chunk fetch.
import { LoginPage } from './pages/auth/LoginPage';
import BiometricUnlock from './pages/auth/BiometricUnlock';
// TenantNotFoundPage renders inside an early return BEFORE the main
// BrowserRouter mounts (the tenantError / !tenantConfig branch). It has to
// resolve synchronously — there's no Suspense boundary above the early
// return to absorb a suspension.
import { TenantNotFoundPage } from './pages/public/TenantNotFoundPage';

/* ============================================================
 * Lazy-loaded route components
 *
 * Before this split, every page in the app landed in one ~7 MB index.js
 * chunk. Now each page ships in its own chunk that loads on demand. Big
 * wins for cold start on both web (less bandwidth) and the Capacitor
 * shell (less JS to parse/eval before first paint), plus much better
 * cache behavior for returning users when only a few page sources move.
 * ============================================================ */

// --- Public marketing
const Homepage = lazy(() =>
  import('./pages/public/Homepage').then((m) => ({ default: m.Homepage })),
);
const Pricing = lazy(() => import('./pages/public/Pricing'));
const BroadbandLabelTool = lazy(() => import('./pages/public/BroadbandLabelTool'));
const PonCalculator = lazy(() => import('./pages/public/PonCalculator'));
const FiberColorCode = lazy(() => import('./pages/public/FiberColorCode'));
const ToolsIndex = lazy(() => import('./pages/public/ToolsIndex'));
const Privacy = lazy(() => import('./pages/public/Privacy'));
const Terms = lazy(() => import('./pages/public/Terms'));
const Contact = lazy(() => import('./pages/public/Contact'));
const BlogListPage = lazy(() =>
  import('./pages/public/BlogListPage').then((m) => ({ default: m.BlogListPage })),
);
const BlogPostPage = lazy(() =>
  import('./pages/public/BlogPostPage').then((m) => ({ default: m.BlogPostPage })),
);
// TenantStatusPage and IncidentDetailPage live in the same module; Vite
// dedupes the dynamic import so both lazy refs share one chunk.
const TenantStatusPage = lazy(() =>
  import('./pages/public/TenantStatusPage').then((m) => ({ default: m.TenantStatusPage })),
);
const IncidentDetailPage = lazy(() =>
  import('./pages/public/TenantStatusPage').then((m) => ({ default: m.IncidentDetailPage })),
);
const PlatformStatusPage = lazy(() =>
  import('./pages/public/PlatformStatusPage').then((m) => ({ default: m.PlatformStatusPage })),
);
// Public "tech on the way" live-tracking page — reached from a tokenized
// SMS link, renders without auth or tenant config.
const TrackTechnician = lazy(() =>
  import('./pages/public/TrackTechnician').then((m) => ({ default: m.TrackTechnician })),
);
const QrLanding = lazy(() => import('./pages/public/QrLanding'));
const ScheduleServiceVisit = lazy(() =>
  import('./pages/public/ScheduleServiceVisit').then((m) => ({ default: m.ScheduleServiceVisit })),
);
const ServiceabilityVerify = lazy(() => import('./pages/public/ServiceabilityVerify'));
const UnifiedSignup = lazy(() =>
  import('./components/signup').then((m) => ({ default: m.UnifiedSignup })),
);

// --- Auth (non-critical paths — LoginPage + BiometricUnlock stay eager above)
const RegisterPage = lazy(() =>
  import('./pages/auth/RegisterPage').then((m) => ({ default: m.RegisterPage })),
);
const RegisterNewISPPage = lazy(() =>
  import('./pages/auth/RegisterNewISPPage').then((m) => ({ default: m.RegisterNewISPPage })),
);
const RegistrationSuccessPage = lazy(() =>
  import('./pages/auth/RegistrationSuccessPage').then((m) => ({
    default: m.RegistrationSuccessPage,
  })),
);
const RegistrationCanceledPage = lazy(() =>
  import('./pages/auth/RegistrationCanceledPage').then((m) => ({
    default: m.RegistrationCanceledPage,
  })),
);
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));
const GoogleCallback = lazy(() => import('./pages/auth/GoogleCallback'));

// --- Dashboard
const DashboardHome = lazy(() =>
  import('./pages/dashboard/DashboardHome').then((m) => ({ default: m.DashboardHome })),
);
const ServiceAreas = lazy(() => import('./pages/dashboard/ServiceAreas'));
const ServiceAreaDetail = lazy(() =>
  import('./pages/dashboard/ServiceAreaDetail').then((m) => ({ default: m.ServiceAreaDetail })),
);
const NetworkSites = lazy(() => import('./pages/dashboard/NetworkSites'));
const NetworkSiteDetails = lazy(() =>
  import('./pages/dashboard/NetworkSiteDetails').then((m) => ({ default: m.NetworkSiteDetails })),
);
const SitePlans = lazy(() => import('./pages/dashboard/SitePlans'));
const SitePlanDetails = lazy(() =>
  import('./pages/dashboard/SitePlanDetails').then((m) => ({ default: m.SitePlanDetails })),
);
const SiteTemplates = lazy(() => import('./pages/dashboard/SiteTemplates'));
const ServiceTiers = lazy(() => import('./pages/dashboard/ServiceTiers'));
const Departments = lazy(() => import('./pages/dashboard/Departments'));
const Leads = lazy(() => import('./pages/dashboard/Leads'));
const ModuleNotIncluded = lazy(() => import('./pages/dashboard/ModuleNotIncluded'));
const LeadDetails = lazy(() => import('./pages/dashboard/LeadDetails'));
const Analytics = lazy(() => import('./pages/dashboard/Analytics'));
const Settings = lazy(() => import('./pages/dashboard/Settings'));
const ServiceAddOns = lazy(() => import('./pages/dashboard/ServiceAddOns'));
const AddressDetails = lazy(() => import('./pages/dashboard/AddressDetails'));
const AddressExport = lazy(() => import('./pages/dashboard/AddressExport'));
const AddressManagement = lazy(() => import('./pages/dashboard/AddressManagement'));
const BdcImport = lazy(() => import('./pages/dashboard/BdcImport'));
const Reports = lazy(() => import('./pages/dashboard/Reports'));
const ARAgingReport = lazy(() => import('./pages/dashboard/ARAgingReport'));
const PaymentSummaryReport = lazy(() => import('./pages/dashboard/PaymentSummaryReport'));
const PaymentDisputes = lazy(() => import('./pages/dashboard/PaymentDisputes'));
const BillingAdjustments = lazy(() => import('./pages/dashboard/BillingAdjustments'));
const ServiceBundles = lazy(() => import('./pages/dashboard/ServiceBundles'));
const InstallationsList = lazy(() =>
  import('./pages/dashboard/InstallationsList').then((m) => ({ default: m.InstallationsList })),
);
const CreateInstallation = lazy(() =>
  import('./pages/dashboard/CreateInstallation').then((m) => ({ default: m.CreateInstallation })),
);
const EditInstallation = lazy(() =>
  import('./pages/dashboard/EditInstallation').then((m) => ({ default: m.EditInstallation })),
);
const InstallationDetails = lazy(() =>
  import('./pages/dashboard/InstallationDetails').then((m) => ({
    default: m.InstallationDetails,
  })),
);
const InstallationSchedule = lazy(() =>
  import('./pages/dashboard/InstallationSchedule').then((m) => ({
    default: m.InstallationSchedule,
  })),
);
const PendingProvisioning = lazy(() =>
  import('./pages/dashboard/PendingProvisioning').then((m) => ({
    default: m.PendingProvisioning,
  })),
);
const SiteSurveysList = lazy(() =>
  import('./pages/dashboard/SiteSurveysList').then((m) => ({ default: m.SiteSurveysList })),
);
const SiteSurveyDetails = lazy(() =>
  import('./pages/dashboard/SiteSurveyDetails').then((m) => ({ default: m.SiteSurveyDetails })),
);
const CreateSiteSurvey = lazy(() =>
  import('./pages/dashboard/CreateSiteSurvey').then((m) => ({ default: m.CreateSiteSurvey })),
);
const SchedulingCalendar = lazy(() =>
  import('./pages/dashboard/SchedulingCalendar').then((m) => ({ default: m.SchedulingCalendar })),
);
const DispatchBoard = lazy(() => import('./pages/dashboard/DispatchBoard'));
const OperationsCenter = lazy(() => import('./pages/dashboard/OperationsCenter'));
const CreateDropBury = lazy(() =>
  import('./pages/dashboard/CreateDropBury').then((m) => ({ default: m.CreateDropBury })),
);
const DropBuryDetails = lazy(() =>
  import('./pages/dashboard/DropBuryDetails').then((m) => ({ default: m.DropBuryDetails })),
);
const OperationalReports = lazy(() =>
  import('./pages/dashboard/OperationalReports').then((m) => ({ default: m.OperationalReports })),
);
const IncomeVsExpensesReport = lazy(() =>
  import('./pages/dashboard/IncomeVsExpensesReport').then((m) => ({
    default: m.IncomeVsExpensesReport,
  })),
);
const WorkflowQueue = lazy(() => import('./pages/dashboard/WorkflowQueue'));
const ChatApprovalQueue = lazy(() => import('./pages/dashboard/ChatApprovalQueue'));
const Contractors = lazy(() => import('./pages/dashboard/Contractors'));
const EscalationRules = lazy(() => import('./pages/dashboard/EscalationRules'));
const EmailTemplates = lazy(() => import('./pages/dashboard/EmailTemplates'));
const SmsTemplates = lazy(() => import('./pages/dashboard/SmsTemplates'));
const SmsHistory = lazy(() => import('./pages/dashboard/SmsHistory'));
const InvoiceTemplates = lazy(() => import('./pages/dashboard/InvoiceTemplates'));
const FAQ = lazy(() => import('./pages/dashboard/FAQ'));
const Help = lazy(() => import('./pages/dashboard/Help'));
const MyAccount = lazy(() => import('./pages/dashboard/MyAccount'));
const Outages = lazy(() => import('./pages/dashboard/Outages'));
const CreateOutage = lazy(() => import('./pages/dashboard/CreateOutage'));
const StatusPageSettings = lazy(() => import('./pages/dashboard/StatusPageSettings'));
const StatusComponents = lazy(() => import('./pages/dashboard/StatusComponents'));
const StatusIncidents = lazy(() => import('./pages/dashboard/StatusIncidents'));
const CreateStatusIncident = lazy(() => import('./pages/dashboard/CreateStatusIncident'));
const StatusMaintenance = lazy(() => import('./pages/dashboard/StatusMaintenance'));
const Campaigns = lazy(() => import('./pages/dashboard/Campaigns'));
const CreateCampaign = lazy(() => import('./pages/dashboard/CreateCampaign'));
const CampaignABTest = lazy(() => import('./pages/dashboard/campaigns/CampaignABTest'));
const CampaignConversions = lazy(() => import('./pages/dashboard/campaigns/CampaignConversions'));
const CampaignAnalytics = lazy(() => import('./pages/dashboard/campaigns/CampaignAnalytics'));
const PromoCodes = lazy(() => import('./pages/dashboard/promo-codes/PromoCodes'));
const CreatePromoCode = lazy(() => import('./pages/dashboard/promo-codes/CreatePromoCode'));
const EditPromoCode = lazy(() => import('./pages/dashboard/promo-codes/EditPromoCode'));
const PromoCodeDetail = lazy(() => import('./pages/dashboard/promo-codes/PromoCodeDetail'));
const WinBackCampaigns = lazy(() => import('./pages/dashboard/winback/WinBackCampaigns'));
const CreateWinBackCampaign = lazy(
  () => import('./pages/dashboard/winback/CreateWinBackCampaign'),
);
const EditWinBackCampaign = lazy(() => import('./pages/dashboard/winback/EditWinBackCampaign'));
const WinBackCampaignDetail = lazy(
  () => import('./pages/dashboard/winback/WinBackCampaignDetail'),
);
const NurturingWorkflows = lazy(() => import('./pages/dashboard/nurturing/NurturingWorkflows'));
const CreateNurturingWorkflow = lazy(
  () => import('./pages/dashboard/nurturing/CreateNurturingWorkflow'),
);
const EditNurturingWorkflow = lazy(
  () => import('./pages/dashboard/nurturing/EditNurturingWorkflow'),
);
const NurturingWorkflowDetail = lazy(
  () => import('./pages/dashboard/nurturing/NurturingWorkflowDetail'),
);
const WorkflowEnrollments = lazy(
  () => import('./pages/dashboard/nurturing/WorkflowEnrollments'),
);
const Referrals = lazy(() => import('./pages/dashboard/Referrals'));
const CustomerAccounts = lazy(() => import('./pages/dashboard/CustomerAccounts'));
const CreateCustomerAccount = lazy(() => import('./pages/dashboard/CreateCustomerAccount'));
const CustomerAccountDetails = lazy(() => import('./pages/dashboard/CustomerAccountDetails'));
const CustomerImport = lazy(() => import('./pages/dashboard/CustomerImport'));
const MigrationDashboard = lazy(() => import('./pages/dashboard/MigrationDashboard'));
const Equipment = lazy(() => import('./pages/dashboard/Equipment'));
const EquipmentImport = lazy(() => import('./pages/dashboard/EquipmentImport'));
const QrCodes = lazy(() => import('./pages/dashboard/QrCodes'));
const FleetVehicles = lazy(() => import('./pages/dashboard/fleet/Vehicles'));
const FleetVehicleDetail = lazy(() => import('./pages/dashboard/fleet/VehicleDetail'));
const FleetIssues = lazy(() => import('./pages/dashboard/fleet/Issues'));
const FleetReports = lazy(() => import('./pages/dashboard/fleet/FleetReports'));
const FleetServiceHistoryPrint = lazy(() => import('./pages/dashboard/fleet/VehicleServiceHistoryPrint'));
const Materials = lazy(() => import('./pages/dashboard/Materials'));
const Invoices = lazy(() => import('./pages/dashboard/Invoices'));
const InvoiceDetails = lazy(() => import('./pages/dashboard/InvoiceDetails'));
const CreateInvoice = lazy(() => import('./pages/dashboard/CreateInvoice'));
const BillingDashboard = lazy(() => import('./pages/dashboard/BillingDashboard'));
const Payments = lazy(() => import('./pages/dashboard/Payments'));
const PaymentDetails = lazy(() => import('./pages/dashboard/PaymentDetails'));
const RecordPayment = lazy(() => import('./pages/dashboard/RecordPayment'));
const PaymentMethods = lazy(() => import('./pages/dashboard/PaymentMethods'));
const AutopaySettings = lazy(() => import('./pages/dashboard/AutopaySettings'));
const BillRuns = lazy(() => import('./pages/dashboard/BillRuns'));
const TaxRates = lazy(() => import('./pages/dashboard/TaxRates'));
const Credits = lazy(() => import('./pages/dashboard/Credits'));
const Cancellations = lazy(() => import('./pages/dashboard/Cancellations'));
const PaperInvoices = lazy(() => import('./pages/dashboard/PaperInvoices'));
const FiberMapping = lazy(() => import('./pages/dashboard/FiberMapping'));
const ParcelLayers = lazy(() => import('./pages/dashboard/ParcelLayers'));
const Permits = lazy(() => import('./pages/dashboard/Permits'));
const PermitDetails = lazy(() => import('./pages/dashboard/PermitDetails'));
const CreatePermit = lazy(() => import('./pages/dashboard/CreatePermit'));
const PermitSheetEditor = lazy(() => import('./pages/dashboard/PermitSheetEditor'));
const PermitSheetView = lazy(() => import('./pages/dashboard/PermitSheetView'));
const NetworkTopology = lazy(() => import('./pages/dashboard/NetworkTopology'));
const IpamDashboard = lazy(() => import('./pages/dashboard/IpamDashboard'));
const SupportTickets = lazy(() => import('./pages/dashboard/SupportTickets'));
const Messages = lazy(() => import('./pages/dashboard/Messages'));
const TicketDetails = lazy(() => import('./pages/dashboard/TicketDetails'));
const CreateTicket = lazy(() => import('./pages/dashboard/CreateTicket'));
const Compliance = lazy(() => import('./pages/dashboard/Compliance'));
const ComplianceDeadlines = lazy(() => import('./pages/dashboard/ComplianceDeadlines'));
const ComplianceDeadlineDetail = lazy(
  () => import('./pages/dashboard/ComplianceDeadlineDetail'),
);
const ComplianceTemplates = lazy(() => import('./pages/dashboard/ComplianceTemplates'));
const LocateTickets = lazy(() => import('./pages/dashboard/LocateTickets'));
const LocateTicketDetail = lazy(() => import('./pages/dashboard/LocateTicketDetail'));
const CreateLocateTicket = lazy(() => import('./pages/dashboard/CreateLocateTicket'));
const QualificationLists = lazy(() => import('./pages/dashboard/QualificationLists'));
const QualificationListDetail = lazy(() => import('./pages/dashboard/QualificationListDetail'));
const CreateQualificationList = lazy(() => import('./pages/dashboard/CreateQualificationList'));
const CanvassTerritories = lazy(() => import('./pages/dashboard/CanvassTerritories'));
const CanvassTerritoryDetail = lazy(() => import('./pages/dashboard/CanvassTerritoryDetail'));
const CanvassTerritoryForm = lazy(() => import('./pages/dashboard/CanvassTerritoryForm'));
const CanvassDashboard = lazy(() => import('./pages/dashboard/CanvassDashboard'));
const CanvassLanguageBarrierCallbacks = lazy(
  () => import('./pages/dashboard/CanvassLanguageBarrierCallbacks'),
);
const NmsMonitoring = lazy(() => import('./pages/dashboard/NmsMonitoring'));

// --- Settings / Analytics / Inventory
const OnboardingDocuments = lazy(() => import('./pages/settings/OnboardingDocuments'));
const LocationsPage = lazy(() =>
  import('./pages/settings/LocationsPage').then((m) => ({ default: m.LocationsPage })),
);
const ServiceabilityProtection = lazy(() => import('./pages/settings/ServiceabilityProtection'));
const SurveyTemplates = lazy(() => import('./pages/settings/SurveyTemplates'));
const FeedbackDashboard = lazy(() => import('./pages/analytics/FeedbackDashboard'));
const InventoryStockPage = lazy(() =>
  import('./pages/inventory/InventoryStockPage').then((m) => ({
    default: m.InventoryStockPage,
  })),
);
const EquipmentTransferPage = lazy(() =>
  import('./pages/inventory/EquipmentTransferPage').then((m) => ({
    default: m.EquipmentTransferPage,
  })),
);

// --- SuperAdmin
const Tenants = lazy(() => import('./pages/superadmin/Tenants'));
const SuperAdminAnalytics = lazy(() => import('./pages/superadmin/SuperAdminAnalytics'));
const OneCallPatterns = lazy(() => import('./pages/superadmin/OneCallPatterns'));
const PlatformStatusAdmin = lazy(() => import('./pages/superadmin/PlatformStatusAdmin'));
const OutreachEmail = lazy(() => import('./pages/superadmin/OutreachEmail'));
const SmsRegistrations = lazy(() => import('./pages/superadmin/SmsRegistrations'));

// --- Embed
const ServiceAreaEmbed = lazy(() =>
  import('./pages/embed/ServiceAreaEmbed').then((m) => ({ default: m.ServiceAreaEmbed })),
);
const AllServiceAreasEmbed = lazy(() =>
  import('./pages/embed/AllServiceAreasEmbed').then((m) => ({
    default: m.AllServiceAreasEmbed,
  })),
);

// --- Customer Portal
const PortalLogin = lazy(() => import('./pages/portal/PortalLogin'));
const PortalGoogleCallback = lazy(() => import('./pages/portal/PortalGoogleCallback'));
const PortalTenantSelect = lazy(() => import('./pages/portal/PortalTenantSelect'));
const PortalSetup = lazy(() => import('./pages/portal/PortalSetup'));
const PortalVerify = lazy(() => import('./pages/portal/PortalVerify'));
const PortalForgotPassword = lazy(() => import('./pages/portal/PortalForgotPassword'));
const PortalResetPassword = lazy(() => import('./pages/portal/PortalResetPassword'));
const PortalSetPassword = lazy(() => import('./pages/portal/PortalSetPassword'));
const PortalDashboard = lazy(() => import('./pages/portal/PortalDashboard'));
const PortalInvoices = lazy(() => import('./pages/portal/PortalInvoices'));
const PortalInvoiceDetail = lazy(() => import('./pages/portal/PortalInvoiceDetail'));
const PortalPayment = lazy(() => import('./pages/portal/PortalPayment'));
const PortalPaymentMethods = lazy(() => import('./pages/portal/PortalPaymentMethods'));
const PortalAutopay = lazy(() => import('./pages/portal/PortalAutopay'));
const PortalAccount = lazy(() => import('./pages/portal/PortalAccount'));
const PortalPaymentHistory = lazy(() => import('./pages/portal/PortalPaymentHistory'));
const PortalFaq = lazy(() => import('./pages/portal/PortalFaq'));
const PortalServiceTiers = lazy(() => import('./pages/portal/PortalServiceTiers'));
const PortalTickets = lazy(() => import('./pages/portal/PortalTickets'));
const ProcessorTestPage = lazy(() => import('./pages/portal/ProcessorTestPage'));
const PortalActivation = lazy(() => import('./pages/portal/PortalActivation'));
const PortalInstallation = lazy(() => import('./pages/portal/PortalInstallation'));
const PortalInstallationConfirmation = lazy(
  () => import('./pages/portal/PortalInstallationConfirmation'),
);
const PortalInstallationDetail = lazy(() => import('./pages/portal/PortalInstallationDetail'));
const PortalInstallations = lazy(() => import('./pages/portal/PortalInstallations'));
const PortalFeedback = lazy(() =>
  import('./pages/portal/PortalFeedback').then((m) => ({ default: m.PortalFeedback })),
);

// --- Field App (PWA)
const FieldDashboard = lazy(() => import('./pages/field/FieldDashboard'));
const FieldJobDetail = lazy(() => import('./pages/field/FieldJobDetail'));
const FieldMap = lazy(() => import('./pages/field/FieldMap'));
const FieldVehicle = lazy(() => import('./pages/field/FieldVehicle'));
const FieldReportVehicleIssue = lazy(() => import('./pages/field/FieldReportVehicleIssue'));
const FieldEnclosureDetail = lazy(() => import('./pages/field/FieldEnclosureDetail'));
const FieldCamera = lazy(() => import('./pages/field/FieldCamera'));
const FieldProfile = lazy(() => import('./pages/field/FieldProfile'));
const FieldLocateTickets = lazy(() => import('./pages/field/FieldLocateTickets'));
const FieldLocateTicketDetail = lazy(() => import('./pages/field/FieldLocateTicketDetail'));
const FieldCreateTicket = lazy(() => import('./pages/field/FieldCreateTicket'));
const FieldAvailableJobs = lazy(() => import('./pages/field/FieldAvailableJobs'));
const FieldSupportTickets = lazy(() => import('./pages/field/FieldSupportTickets'));
const FieldMessages = lazy(() => import('./pages/field/FieldMessages'));
const FieldMessageThread = lazy(() => import('./pages/field/FieldMessageThread'));
const FieldWork = lazy(() => import('./pages/field/FieldWork'));
const FieldSchedule = lazy(() => import('./pages/field/FieldSchedule'));
const FieldScanner = lazy(() => import('./pages/field/FieldScanner'));
const FieldTicketDetail = lazy(() => import('./pages/field/FieldTicketDetail'));

// --- Contractor Portal
const ContractorLogin = lazy(() => import('./pages/contractor/ContractorLogin'));
const ContractorVerify = lazy(() => import('./pages/contractor/ContractorVerify'));
const ContractorSetPassword = lazy(() => import('./pages/contractor/ContractorSetPassword'));
const ContractorForgotPassword = lazy(
  () => import('./pages/contractor/ContractorForgotPassword'),
);
const ContractorDashboard = lazy(() => import('./pages/contractor/ContractorDashboard'));
const ContractorJobDetail = lazy(() => import('./pages/contractor/ContractorJobDetail'));
const ContractorMap = lazy(() => import('./pages/contractor/ContractorMap'));
const ContractorProfile = lazy(() => import('./pages/contractor/ContractorProfile'));

import './index.css';

// Embed routes are isolated from the main app - no auth/tenant context
function EmbedApp() {
  // Embeds sit inside a tenant's page and always render light; see
  // useTenant, which skips the dashboard theme for /embed/* documents.
  // Clearing the class here too covers the moment before tenant config
  // loads, and tells the browser to draw form controls in light mode.
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    document.documentElement.style.colorScheme = 'light';
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AnalyticsTracker />
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/embed/service-area/:id" element={<ServiceAreaEmbed />} />
            <Route path="/embed/service-areas" element={<AllServiceAreasEmbed />} />
            <Route path="/embed/signup" element={<UnifiedSignup mode="compact" />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

// Handle subdomain redirects before React renders
// This runs synchronously on module load to avoid flash of wrong content
(function handleSubdomainRedirect() {
  const hostname = window.location.hostname.toLowerCase();
  const pathname = window.location.pathname;

  if (hostname.startsWith('portal.') && !pathname.startsWith('/portal')) {
    window.location.replace('/portal' + pathname + window.location.search);
  } else if (hostname.startsWith('contractor.') && !pathname.startsWith('/contractor')) {
    window.location.replace('/contractor' + pathname + window.location.search);
  }
})();

// Main app with full tenant/auth context
function MainApp() {
  const { tenantConfig, tenantLoading, tenantError } = useTenant();

  // Enable idle timeout for automatic logout
  useIdleTimeout();

  const pathname = window.location.pathname;

  const isPortalRoute = pathname.startsWith('/portal');
  const isFieldRoute = pathname.startsWith('/field');
  const isContractorRoute = pathname.startsWith('/contractor');
  const isQrRoute = pathname.startsWith('/qr/');
  // Public tech-tracking links (tokenized, from SMS) need no tenant config.
  const isTrackRoute = pathname.startsWith('/track/');
  // Customer ticket self-scheduling: a tokenized link, no tenant context needed up front
  const isScheduleRoute = pathname.startsWith('/schedule/');
  const isAuthRoute = pathname.startsWith('/auth/') ||
                      pathname === '/login' ||
                      pathname === '/register' ||
                      pathname === '/register-isp' ||
                      pathname === '/register/success' ||
                      pathname === '/register/canceled' ||
                      pathname === '/forgot-password' ||
                      pathname === '/reset-password' ||
                      pathname === '/verify-email' ||
                      // BiometricUnlock screen — native shell launches here when
                      // Face ID / Touch ID is enrolled. The page doesn't need
                      // tenant config (it just runs biometric + a token refresh)
                      // and gating it on tenantConfig caused it to render
                      // TenantNotFoundPage instead, which then crashed because
                      // it uses useNavigate outside the BrowserRouter scope of
                      // the early return.
                      pathname === '/unlock';

  // Public marketing routes that don't require tenant config or backend
  const isPublicMarketingRoute = pathname === '/' ||
                                  pathname.startsWith('/blog') ||
                                  pathname === '/pricing' ||
                                  // Free tools and their index: no account, no tenant.
                                  pathname === '/tools' ||
                                  pathname.startsWith('/tools/') ||
                                  pathname === '/contact' ||
                                  pathname === '/privacy' ||
                                  pathname === '/terms' ||
                                  pathname.startsWith('/status') ||
                                  pathname === '/platform-status';

  // For portal, field, contractor, QR, track, auth, and public marketing routes, don't block on tenant loading
  if (!isPortalRoute && !isFieldRoute && !isContractorRoute && !isQrRoute && !isTrackRoute && !isScheduleRoute && !isAuthRoute && !isPublicMarketingRoute) {
    // Show loading screen while tenant config is being fetched
    if (tenantLoading) {
      return <LoadingScreen message="Loading application..." />;
    }

    // Show error screen if tenant config failed to load.
    // BrowserRouter wrap is required because TenantNotFoundPage uses
    // useNavigate() for the in-WebView lookup-success path (added to fix
    // the Safari-escape bug on native). Without a Router in scope React
    // throws "useNavigate may only be used in the context of a <Router>"
    // and the whole subtree renders blank. The "real" BrowserRouter below
    // for the normal app body is rendered separately; React Router 6
    // tolerates the two not sharing state because this branch always
    // returns early.
    if (tenantError || !tenantConfig) {
      return (
        <BrowserRouter>
          <TenantNotFoundPage error={tenantError} />
        </BrowserRouter>
      );
    }
  }

  return (
    <ErrorBoundary>
      <AppProviders>
      <BrowserRouter>
        <ScrollToTop />
        <AnalyticsTracker />
        <LimitExceededModal />
        {/* Listens for pushNotificationActionPerformed events the field
            shell emits as `push:tapped` and navigates to data.deepLink.
            Must live inside the BrowserRouter for useNavigate to work. */}
        <PushTapHandler />
        <Toaster
          position="top-right"
          // Push the toast stack below the iOS notch / Dynamic Island.
          // The default top:16px lands toasts behind the Island on
          // iPhone 14 Pro and later. env(safe-area-inset-top) is the
          // hardware inset (≈59px on Dynamic Island devices, 47px on
          // notch devices, 0 in browsers / on web). The calc() adds
          // 8px of breathing room below the cutout so toasts don't
          // crowd it.
          containerStyle={{
            top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
          }}
          toastOptions={{
            duration: 3000,
            style: {
              background: 'var(--color-surface)',
              color: 'var(--color-text)',
              border: '1px solid var(--color-border)',
            },
            success: {
              iconTheme: {
                primary: 'var(--color-success)',
                secondary: 'var(--color-surface)',
              },
            },
            error: {
              iconTheme: {
                primary: 'var(--color-error)',
                secondary: 'var(--color-surface)',
              },
            },
          }}
        />

        {/* Suspense boundary for code-split route chunks. Layouts that want
            to keep their chrome visible during in-app navigation should
            wrap their own <Outlet /> in a nested <Suspense> (see
            FieldLayout for the bottom-nav case). The top-level fallback
            here is what shows on a cold cross-section navigation
            (e.g. /dashboard → /field) where the layout itself changes. */}
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
          {/* Public Routes */}
          <Route element={<PublicLayout />}>
            {/* When the app is running inside a Capacitor native shell, the
                marketing homepage isn't the right landing — boot directly
                into the app the shell was built for: the customer portal
                (VITE_NATIVE_APP=portal) or the Dispatch field worker app,
                the latter via the biometric unlock gate if it's enrolled and
                not yet passed this session. On web this falls through to
                <Homepage />. */}
            <Route
              path="/"
              element={
                isNativeApp()
                  ? <Navigate
                      to={
                        nativeAppMode() === 'portal'
                          ? '/portal'
                          : isBiometricEnabled() && !useAppStore.getState().biometricUnlocked
                            ? '/unlock'
                            : '/field'
                      }
                      replace
                    />
                  : <Homepage />
              }
            />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/tools" element={<ToolsIndex />} />
            <Route path="/tools/broadband-label" element={<BroadbandLabelTool />} />
            <Route path="/tools/pon-calculator" element={<PonCalculator />} />
            <Route path="/tools/fiber-color-code" element={<FiberColorCode />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/status" element={<TenantStatusPage />} />
            <Route path="/status/incident/:id" element={<IncidentDetailPage />} />
            <Route path="/platform-status" element={<PlatformStatusPage />} />
            <Route path="/service-areas/:id" element={<div>Service Area Detail - Coming Soon</div>} />
          </Route>

          {/* Tenant Signup Route (standalone, no header/footer - tenant branded) */}
          <Route path="/signup" element={<UnifiedSignup mode="full" />} />
          <Route path="/check-address/verify" element={<ServiceabilityVerify />} />

          {/* Auth Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/unlock" element={<BiometricUnlock />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/register-isp" element={<RegisterNewISPPage />} />
          <Route path="/register/success" element={<RegistrationSuccessPage />} />
          <Route path="/register/canceled" element={<RegistrationCanceledPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />

          {/* Embed Routes are handled separately at the top of App() to bypass auth/tenant logic */}

          {/* QR Code Landing Page (standalone, shows different content based on auth) */}
          <Route path="/qr/:tenantSubdomain/:code" element={<QrLanding />} />

          {/* Public "tech on the way" live-tracking page (tokenized SMS link, no auth) */}
          <Route path="/track/:token" element={<TrackTechnician />} />

          {/* Public ticket self-scheduling page (tokenized SMS/email link, no auth) */}
          <Route path="/schedule/:token" element={<ScheduleServiceVisit />} />

          {/* Dashboard Routes */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <DashboardLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<DashboardHome />} />
            <Route path="service-areas" element={<ServiceAreas />} />
            <Route path="service-areas/:id" element={<ServiceAreaDetail />} />
            <Route path="network-sites" element={<NetworkSites />} />
            <Route path="network-sites/:id" element={<NetworkSiteDetails />} />
            <Route path="network-topology" element={<NetworkTopology />} />
            <Route path="ip-addresses" element={<IpamDashboard />} />
            <Route path="nms" element={<NmsMonitoring />} />
            {/* Legacy routes redirect to unified NMS page */}
            <Route path="adtran" element={<Navigate to="/dashboard/nms" replace />} />
            <Route path="calix" element={<Navigate to="/dashboard/nms" replace />} />
            <Route path="uisp" element={<Navigate to="/dashboard/nms" replace />} />
            <Route path="fiber-mapping" element={<FiberMapping />} />
            <Route path="parcel-layers" element={<ParcelLayers />} />
            <Route path="permits" element={<Permits />} />
            <Route path="permits/new" element={<CreatePermit />} />
            <Route path="permits/:id/edit" element={<CreatePermit />} />
            <Route path="permits/:id" element={<PermitDetails />} />
            <Route path="site-plans" element={<SitePlans />} />
            <Route path="site-plans/:id" element={<SitePlanDetails />} />
            <Route path="site-templates" element={<SiteTemplates />} />
            <Route path="service-tiers" element={<ServiceTiers />} />
            <Route path="departments" element={<Departments />} />
            <Route path="leads" element={<Leads />} />
            <Route path="upgrade" element={<ModuleNotIncluded />} />
            <Route path="leads/:id" element={<LeadDetails />} />
            <Route path="installations" element={<InstallationsList />} />
            <Route path="installations/new" element={<CreateInstallation />} />
            <Route path="installations/schedule" element={<InstallationSchedule />} />
            <Route path="installations/:id/edit" element={<EditInstallation />} />
            <Route path="installations/:id" element={<InstallationDetails />} />
            <Route path="surveys" element={<SiteSurveysList />} />
            <Route path="surveys/new" element={<CreateSiteSurvey />} />
            <Route path="surveys/:id" element={<SiteSurveyDetails />} />
            <Route path="scheduling" element={<SchedulingCalendar />} />
            <Route path="dispatch" element={<DispatchBoard />} />
            <Route path="pending-provisioning" element={<PendingProvisioning />} />
            <Route path="operations" element={<OperationsCenter />} />
            <Route path="drop-bury/new" element={<CreateDropBury />} />
            <Route path="drop-bury/:id" element={<DropBuryDetails />} />
            <Route path="workflow-queue" element={<WorkflowQueue />} />
            <Route path="chat-approvals" element={<ChatApprovalQueue />} />
            <Route path="contractors" element={<Contractors />} />
            <Route path="accounts" element={<CustomerAccounts />} />
            <Route path="accounts/new" element={<CreateCustomerAccount />} />
            <Route path="accounts/import" element={<CustomerImport />} />
            <Route path="accounts/migration" element={<MigrationDashboard />} />
            <Route path="accounts/:id" element={<CustomerAccountDetails />} />

            {/* Billing */}
            <Route path="billing" element={<BillingDashboard />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="invoices/new" element={<CreateInvoice />} />
            <Route path="invoices/:id" element={<InvoiceDetails />} />
            <Route path="payments" element={<Payments />} />
            <Route path="payments/new" element={<RecordPayment />} />
            <Route path="payments/:id" element={<PaymentDetails />} />
            <Route path="payment-methods" element={<PaymentMethods />} />
            <Route path="autopay-settings" element={<AutopaySettings />} />
            <Route path="bill-runs" element={<BillRuns />} />
            <Route path="tax-rates" element={<TaxRates />} />
            <Route path="credits" element={<Credits />} />
            <Route path="paper-invoices" element={<PaperInvoices />} />
            <Route path="cancellations" element={<Cancellations />} />
            <Route path="equipment" element={<Equipment />} />
            <Route path="equipment/import" element={<EquipmentImport />} />
            <Route path="equipment/qr-codes" element={<QrCodes />} />
            <Route path="materials" element={<Materials />} />
            <Route path="fleet" element={<FleetVehicles />} />
            <Route path="fleet/issues" element={<FleetIssues />} />
            <Route path="fleet/reports" element={<FleetReports />} />
            <Route path="fleet/:id" element={<FleetVehicleDetail />} />
            <Route path="tickets" element={<SupportTickets />} />
            <Route path="messages" element={<Messages />} />
            <Route path="messages/:id" element={<Messages />} />
            <Route path="tickets/new" element={<CreateTicket />} />
            <Route path="tickets/:id" element={<TicketDetails />} />
            <Route path="compliance" element={<Compliance />} />
            <Route path="compliance/deadlines" element={<ComplianceDeadlines />} />
            <Route path="compliance/deadlines/:id" element={<ComplianceDeadlineDetail />} />
            <Route path="compliance/templates" element={<ComplianceTemplates />} />
            <Route path="locate-tickets" element={<LocateTickets />} />
            <Route path="locate-tickets/new" element={<CreateLocateTicket />} />
            <Route path="locate-tickets/:id" element={<LocateTicketDetail />} />
            <Route path="locate-tickets/:id/edit" element={<CreateLocateTicket />} />
            <Route path="qualification-lists" element={<QualificationLists />} />
            <Route path="qualification-lists/new" element={<CreateQualificationList />} />
            <Route path="qualification-lists/:id" element={<QualificationListDetail />} />
            <Route path="qualification-lists/:id/edit" element={<CreateQualificationList />} />
            <Route path="canvass" element={<CanvassDashboard />} />
            <Route path="canvass/territories" element={<CanvassTerritories />} />
            <Route path="canvass/territories/new" element={<CanvassTerritoryForm />} />
            <Route path="canvass/territories/:id" element={<CanvassTerritoryDetail />} />
            <Route path="canvass/territories/:id/edit" element={<CanvassTerritoryForm />} />
            <Route path="canvass/language-callbacks" element={<CanvassLanguageBarrierCallbacks />} />
            <Route path="escalation-rules" element={<EscalationRules />} />
            <Route path="email-templates" element={<EmailTemplates />} />
            <Route path="sms-templates" element={<SmsTemplates />} />
            <Route path="sms-history" element={<SmsHistory />} />
            <Route path="invoice-templates" element={<InvoiceTemplates />} />
            <Route path="outages" element={<Outages />} />
            <Route path="outages/new" element={<CreateOutage />} />
            <Route path="outages/:id/edit" element={<CreateOutage />} />
            {/* Status Page */}
            <Route path="status-page" element={<StatusPageSettings />} />
            <Route path="status-page/components" element={<StatusComponents />} />
            <Route path="status-page/incidents" element={<StatusIncidents />} />
            <Route path="status-page/incidents/new" element={<CreateStatusIncident />} />
            <Route path="status-page/incidents/:id" element={<IncidentDetailPage />} />
            <Route path="status-page/maintenance" element={<StatusMaintenance />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="campaigns/new" element={<CreateCampaign />} />
            <Route path="campaigns/:id/edit" element={<CreateCampaign />} />
            <Route path="campaigns/:id/ab-test" element={<CampaignABTest />} />
            <Route path="campaigns/:id/conversions" element={<CampaignConversions />} />
            <Route path="campaigns/analytics" element={<CampaignAnalytics />} />
            {/* Promo Codes */}
            <Route path="promo-codes" element={<PromoCodes />} />
            <Route path="promo-codes/new" element={<CreatePromoCode />} />
            <Route path="promo-codes/:id" element={<PromoCodeDetail />} />
            <Route path="promo-codes/:id/edit" element={<EditPromoCode />} />
            {/* Win-Back Campaigns */}
            <Route path="winback-campaigns" element={<WinBackCampaigns />} />
            <Route path="winback-campaigns/new" element={<CreateWinBackCampaign />} />
            <Route path="winback-campaigns/:id" element={<WinBackCampaignDetail />} />
            <Route path="winback-campaigns/:id/edit" element={<EditWinBackCampaign />} />
            {/* Lead Nurturing */}
            <Route path="nurturing" element={<NurturingWorkflows />} />
            <Route path="nurturing/new" element={<CreateNurturingWorkflow />} />
            <Route path="nurturing/:id" element={<NurturingWorkflowDetail />} />
            <Route path="nurturing/:id/edit" element={<EditNurturingWorkflow />} />
            <Route path="nurturing/:id/enrollments" element={<WorkflowEnrollments />} />
            {/* Referrals */}
            <Route path="referrals" element={<Referrals />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="analytics/feedback" element={<FeedbackDashboard />} />
            <Route path="survey-templates" element={<SurveyTemplates />} />
            <Route path="reports" element={<Reports />} />
            <Route path="reports/ar-aging" element={<ARAgingReport />} />
            <Route path="reports/payment-summary" element={<PaymentSummaryReport />} />
            <Route path="payment-disputes" element={<PaymentDisputes />} />
            <Route path="billing-adjustments" element={<BillingAdjustments />} />
            <Route path="operational-reports" element={<OperationalReports />} />
            <Route path="income-vs-expenses" element={<IncomeVsExpensesReport />} />
            <Route path="addresses" element={<AddressManagement />} />
            <Route path="addresses/export" element={<AddressExport />} />
            <Route path="addresses/:id" element={<AddressDetails />} />
            <Route path="bdc-import" element={<BdcImport />} />
            <Route path="settings" element={<Settings />} />
            <Route path="settings/add-ons" element={<ServiceAddOns />} />
            <Route path="settings/onboarding-documents" element={<OnboardingDocuments />} />
            <Route path="settings/locations" element={<LocationsPage />} />
            <Route
              path="settings/serviceability-protection"
              element={
                <RequirePermission
                  permission="serviceability_protection.view"
                  fallback={<div className="p-6 text-gray-600">You don&apos;t have access to this page.</div>}
                >
                  <ServiceabilityProtection />
                </RequirePermission>
              }
            />
            <Route path="inventory/stock" element={<InventoryStockPage />} />
            <Route path="inventory/transfer" element={<EquipmentTransferPage />} />
            <Route path="service-bundles" element={<ServiceBundles />} />
            <Route path="faq" element={<FAQ />} />
            {/* User guide — root shows the welcome panel; slug param accepts
                multi-segment paths like "tickets/creating-a-ticket". */}
            <Route path="help" element={<Help />} />
            <Route path="help/*" element={<Help />} />
            <Route path="account" element={<MyAccount />} />
          </Route>

          {/* Printable vehicle service history (outside the dashboard layout so only the packet prints) */}
          <Route
            path="/dashboard/fleet/:id/service-history"
            element={
              <PrivateRoute>
                <RequirePermission
                  permission="fleet.view"
                  fallback={<div className="p-6 text-gray-600">You don&apos;t have access to this page.</div>}
                >
                  <FleetServiceHistoryPrint />
                </RequirePermission>
              </PrivateRoute>
            }
          />

          {/* Permit Sheet Editor/View (Full-screen, outside dashboard layout) */}
          <Route
            path="/dashboard/permits/:permitId/sheets/new"
            element={
              <PrivateRoute>
                <PermitSheetEditor />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard/permits/:permitId/sheets/:sheetId"
            element={
              <PrivateRoute>
                <PermitSheetView />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard/permits/:permitId/sheets/:sheetId/edit"
            element={
              <PrivateRoute>
                <PermitSheetEditor />
              </PrivateRoute>
            }
          />

          {/* Super Admin Routes */}
          <Route
            path="/superadmin"
            element={
              <PrivateRoute requiredRole="SuperAdmin">
                <DashboardLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<Tenants />} />
            <Route path="tenants" element={<Tenants />} />
            <Route path="analytics" element={<SuperAdminAnalytics />} />
            <Route path="onecall-patterns" element={<OneCallPatterns />} />
            <Route path="platform-status" element={<PlatformStatusAdmin />} />
            <Route path="outreach" element={<OutreachEmail />} />
            <Route path="sms-registrations" element={<SmsRegistrations />} />
          </Route>

          {/* Customer Portal Routes */}
          {/* Public Feedback Route (outside PortalLayout for standalone experience) */}
          <Route path="/portal/feedback" element={<PortalFeedback />} />
          <Route path="/portal" element={<PortalLayout />}>
            <Route index element={<PortalLogin />} />
            <Route path="login" element={<PortalLogin />} />
            <Route path="setup" element={<PortalSetup />} />
            <Route path="verify" element={<PortalVerify />} />
            <Route path="forgot-password" element={<PortalForgotPassword />} />
            <Route path="reset-password" element={<PortalResetPassword />} />
            <Route path="auth/google/callback" element={<PortalGoogleCallback />} />
            <Route path="select-tenant" element={<PortalTenantSelect />} />
            {/* Protected Portal Routes */}
            <Route element={<PortalPrivateRoute />}>
              <Route path="set-password" element={<PortalSetPassword />} />
              <Route path="activation" element={<PortalActivation />} />
              <Route path="dashboard" element={<PortalDashboard />} />
              <Route path="account" element={<PortalAccount />} />
              <Route path="invoices" element={<PortalInvoices />} />
              <Route path="invoices/:id" element={<PortalInvoiceDetail />} />
              <Route path="pay" element={<PortalPayment />} />
              <Route path="payment-methods" element={<PortalPaymentMethods />} />
              <Route path="autopay" element={<PortalAutopay />} />
              <Route path="payments" element={<PortalPaymentHistory />} />
              <Route path="faqs" element={<PortalFaq />} />
              <Route path="my-plan" element={<PortalServiceTiers />} />
              <Route path="tickets" element={<PortalTickets />} />
              <Route path="installation" element={<PortalInstallation />} />
              <Route path="installation/confirmation" element={<PortalInstallationConfirmation />} />
              <Route path="installation/:id" element={<PortalInstallationDetail />} />
              <Route path="installations" element={<PortalInstallations />} />
            </Route>
          </Route>

          {/* Field App Routes (PWA for field technicians) */}
          <Route
            path="/field"
            element={
              <PrivateRoute>
                <FieldLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<FieldDashboard />} />
            <Route path="work" element={<FieldWork />} />
            <Route path="schedule" element={<FieldSchedule />} />
            <Route path="jobs/:jobType/:id" element={<FieldJobDetail />} />
            <Route path="available-jobs" element={<FieldAvailableJobs />} />
            <Route path="locate-tickets" element={<FieldLocateTickets />} />
            <Route path="locate-tickets/:id" element={<FieldLocateTicketDetail />} />
            <Route path="support-tickets" element={<FieldSupportTickets />} />
            <Route path="messages" element={<FieldMessages />} />
            <Route path="messages/:id" element={<FieldMessageThread />} />
            <Route path="map" element={<FieldMap />} />
            <Route path="vehicle" element={<FieldVehicle />} />
            <Route path="vehicle/report" element={<FieldReportVehicleIssue />} />
            <Route path="enclosure/:id" element={<FieldEnclosureDetail />} />
            <Route path="tickets/create" element={<FieldCreateTicket />} />
            <Route path="tickets/:id" element={<FieldTicketDetail />} />
            <Route path="camera" element={<FieldCamera />} />
            <Route path="scanner" element={<FieldScanner />} />
            <Route path="profile" element={<FieldProfile />} />
          </Route>

          {/* Contractor Portal Routes */}
          <Route path="/contractor" element={<ContractorLayout />}>
            <Route index element={<ContractorLogin />} />
            <Route path="verify" element={<ContractorVerify />} />
            <Route path="forgot-password" element={<ContractorForgotPassword />} />
            {/* Protected Contractor Routes */}
            <Route element={<ContractorPrivateRoute />}>
              <Route path="set-password" element={<ContractorSetPassword />} />
              <Route path="dashboard" element={<ContractorDashboard />} />
              <Route path="jobs/:type/:id" element={<ContractorJobDetail />} />
              <Route path="map" element={<ContractorMap />} />
              <Route path="profile" element={<ContractorProfile />} />
            </Route>
          </Route>

          {/* Debug/Test Routes (no navigation, direct URL access only) */}
          <Route path="/portal/processor-test" element={<ProcessorTestPage />} />

          {/* Quick pay redirect */}
          <Route path="/pay" element={<Navigate to="/portal" replace />} />

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </AppProviders>
    </ErrorBoundary>
  );
}

// Root App component that selects between embed and main app
function App() {
  // Check for embed routes - these are completely isolated from main app
  const isEmbedRoute = window.location.pathname.startsWith('/embed/');

  if (isEmbedRoute) {
    return <EmbedApp />;
  }

  return <MainApp />;
}

export default App;
